import { clearSessionPreservingPrintSettings } from "../../utils/orderPrintSettings";
import { useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const isTokenValid = (token) => {
  if (!token) return false;

  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return false;

    const base64 = payloadPart
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payloadPart.length / 4) * 4, "=");
    const payload = JSON.parse(atob(base64));
    const expiresAt = Number(payload.exp) * 1000;

    return Number.isFinite(expiresAt) && expiresAt > Date.now();
  } catch {
    return false;
  }
};

const storeLoginData = (data) => {
  if (!data?.token || !data?.user) {
    throw new Error("Phản hồi đăng nhập không hợp lệ.");
  }

  const { user, token } = data;
  localStorage.setItem("token", token);

  const fields = {
    LoaiCH: user.LoaiCH,
    TenCuaHang: user.TenCuaHang,
    IDCH: user.IDCH,
    MaNV: user.MaNV,
    TenUser: user.TenUser,
    MaPB: user.MaPB,
    stk: user.STKNganHang,
    bank_name: user.NganHang,
    Address: user.DiaChi,
    Phone: user.SoDienThoai,
    LamTron:
      typeof user.LamTron === "string" ? user.LamTron.trim() : user.LamTron,
    IpBanhNgot:
      typeof user.IpBanhNgot === "string" ? user.IpBanhNgot.trim() : "",
    IpMayTinh:
      typeof user.IpMayTinh === "string" ? user.IpMayTinh.trim() : "",
    IpThuNgan:
      typeof user.IpThuNgan === "string"
        ? user.IpThuNgan.trim()
        : typeof user.IpMayIn === "string"
          ? user.IpMayIn.trim()
          : "",
    IpBep: Array.isArray(user.IpBep)
      ? typeof user.IpBep[0] === "string"
        ? user.IpBep[0].trim()
        : ""
      : typeof user.IpBep === "string"
        ? user.IpBep.trim()
        : "",
  };

  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      localStorage.setItem(key, String(value));
    }
  });

  localStorage.removeItem("isAdmin");
  localStorage.removeItem("isBakery");
  localStorage.removeItem("HCM_ADMIN");

  const maPB = Number(user.MaPB);
  if ([2, 3, 4, 16, 145, 170, 187, 250, 321].includes(maPB)) {
    localStorage.setItem("isAdmin", "true");
  } else if (maPB === 142) {
    localStorage.setItem("HCM_ADMIN", "true");
  } else if (Number.isFinite(maPB)) {
    localStorage.setItem("isBakery", "true");
  }
};

const ImportDiemCV = () => {
  const location = useLocation();

  const userFromUrl = useMemo(() => {
    // hỗ trợ cả BrowserRouter (?user=...) và HashRouter (#/path?user=...)
    const search =
      location.search ||
      (location.hash?.includes("?")
        ? location.hash.slice(location.hash.indexOf("?"))
        : "");

    const rawUser = new URLSearchParams(search).get("user")?.trim();
    if (!rawUser) return "";
    return rawUser.includes("@") ? rawUser : `${rawUser}@doticom.vn`;
  }, [location.search, location.hash]);

  const hasUserFromUrl = Boolean(userFromUrl);

  // Extract from and to dates from URL params
  const datesFromUrl = useMemo(() => {
    const search =
      location.search ||
      (location.hash?.includes("?")
        ? location.hash.slice(location.hash.indexOf("?"))
        : "");

    const params = new URLSearchParams(search);
    const fromParam = params.get("from");
    const toParam = params.get("to");

    return {
      from: fromParam,
      to: toParam,
    };
  }, [location.search, location.hash]);

  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);

  // Filter states
  const [fromDate, setFromDate] = useState(() => {
    if (datesFromUrl.from) return datesFromUrl.from;
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    return `${yyyy}-${mm}-01`;
  });
  const [toDate, setToDate] = useState(() => {
    if (datesFromUrl.to) return datesFromUrl.to;
    return new Date().toISOString().split("T")[0];
  }); //use today's date
  const [usernames, setUsernames] = useState(userFromUrl);
  const [viewAll, setViewAll] = useState(!hasUserFromUrl);
  const [evaluateOnly, setEvaluateOnly] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [showSavingModal, setShowSavingModal] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0 });
  const [savingLog, setSavingLog] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    isTokenValid(localStorage.getItem("token")),
  );
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const canSaveToDatabase =
    isAuthenticated && localStorage.getItem("MaPB") === "321";

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  const limit = 50;
  const BATCH_PAGE_SIZE = 2;
  const totalPages = Math.ceil(totalCount / limit);

  const getAuthHeaders = (baseHeaders = {}) => {
    const token =
      localStorage.getItem("apiToken") || process.env.REACT_APP_API_TOKEN || "";
    if (!token) return baseHeaders;
    return {
      ...baseHeaders,
      Authorization: `Bearer ${token}`,
    };
  };

  const normalizeUsernamesForApi = (value = "") => {
    return value
      .split(",")
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean)
      .join(",");
  };

  const fetchWorkOrdersPage = async (page = 1) => {
    const offset = (page - 1) * limit;

    const normalizedUsernames = normalizeUsernamesForApi(usernames);

    const usernameParam = viewAll
      ? "admin@doticom.vn"
      : normalizedUsernames || "admin@doticom.vn";

    const params = new URLSearchParams({
      username: usernameParam,
      from: fromDate,
      to: toDate,
      offset: String(offset),
      limit: String(limit),
    });

    if (evaluateOnly) {
      params.append("evaluate", "true");
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/api/fastwork/workorders?${params.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: Không thể tải dữ liệu`);
    }

    const data = await response.json();

    return {
      list: data.result ?? [],
      count: data.count ?? 0,
    };
  };

  const fetchWorkOrders = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const { list, count } = await fetchWorkOrdersPage(page);
      setWorkOrders(list);
      setTotalCount(count);
    } catch (err) {
      setError(err.message || "Không thể kết nối đến server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkOrders(currentPage);
    // Chỉ tải lại khi đổi trang; bộ lọc dùng nút Tìm kiếm.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.removeItem("token");
    }
  }, [isAuthenticated]);

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
    setLoginError("");
  };

  const handleLogout = () => {
    clearSessionPreservingPrintSettings();
    window.location.reload();
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");

    const username = loginUsername.trim();
    if (!username || !loginPassword) {
      setLoginError("Vui lòng nhập tên đăng nhập và mật khẩu.");
      return;
    }

    setLoggingIn(true);

    try {
      const isAdminLogin = username === "admin";
      const endpoint = isAdminLogin ? "/admin-login" : "/login";
      const body = isAdminLogin
        ? { username, password: loginPassword }
        : { TenUser: username, Password: loginPassword };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );

      let data;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          data?.message || "Đăng nhập không thành công. Vui lòng thử lại.",
        );
      }

      storeLoginData(data);
      setLoginPassword("");
      setIsAuthenticated(true);
      setShowLoginModal(false);
    } catch (err) {
      setLoginError(
        err.message ||
          "Không thể kết nối đến máy chủ. Vui lòng thử lại sau.",
      );
    } finally {
      setLoggingIn(false);
    }
  };

  const handleSearch = () => {
    if (currentPage === 1) {
      fetchWorkOrders(1);
      return;
    }
    setCurrentPage(1);
  };
  useEffect(() => {
    if (hasUserFromUrl) {
      setUsernames(userFromUrl);
      setViewAll(false);
    }
  }, [hasUserFromUrl, userFromUrl]);

  // Update dates from URL params
  useEffect(() => {
    if (datesFromUrl.from) {
      setFromDate(datesFromUrl.from);
    }
    if (datesFromUrl.to) {
      setToDate(datesFromUrl.to);
    }
  }, [datesFromUrl.from, datesFromUrl.to]);

  const handleUsernamesChange = (e) => {
    const value = e.target.value;
    setUsernames(value);

    // Automatically uncheck "view all" when username is entered
    if (value.trim()) {
      setViewAll(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatDate = (dateString) => {
    // Handle both timestamp and date string formats
    if (!dateString) return "N/A";
    const date =
      typeof dateString === "number"
        ? new Date(dateString)
        : new Date(dateString);
    return date.toLocaleString("vi-VN");
  };

  const formatDelayHours = (hours) => {
    if (!hours || hours <= 0) return null;

    // Làm tròn 3 chữ số thập phân
    const roundedHours = Math.round(hours * 1000) / 1000;

    // Nếu nhỏ hơn 1 giờ → chỉ hiển thị phút
    if (roundedHours < 1) {
      const minutes = Math.round(roundedHours * 60);
      return `${minutes} phút`;
    }

    const days = Math.floor(roundedHours / 24);
    const remainingHoursFloat = roundedHours % 24;

    const wholeHours = Math.floor(remainingHoursFloat);
    const minutes = Math.round((remainingHoursFloat - wholeHours) * 60);

    let result = "";

    if (days > 0) result += `${days} ngày `;
    if (wholeHours > 0) result += `${wholeHours} giờ `;
    if (minutes > 0) result += `${minutes} phút`;

    return result.trim();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Hoàn thành":
        return "bg-success text-white";
      case "Chờ đánh giá":
        return "bg-success text-dark";
      case "Đang thực hiện":
        return "bg-primary text-white";
      case "Tạm dừng":
        return "bg-danger text-white";
      case "Hủy":
        return "bg-danger text-white";
      case "Chưa thực hiện":
        return "bg-danger text-white";
      default:
        return "bg-secondary text-white";
    }
  };

  const handleWorkOrderClick = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedWorkOrder(null);
  };

  const normalizeUserCode = (email) => {
    if (!email) return "";
    const atIndex = email.indexOf("@");
    return atIndex > 0 ? email.slice(0, atIndex) : email;
  };

  const isDateInRange = (dateString, startDate, endDate) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const start = new Date(startDate);
    const end = new Date(endDate);
    // Compare only dates, not time
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const startOnly = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    );
    const endOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return dateOnly >= startOnly && dateOnly <= endOnly;
  };

  const filteredWorkOrders = workOrders.filter((wo) =>
    isDateInRange(wo.wo_date_end, fromDate, toDate),
  );

  const buildEvaluationRecords = (source = workOrders) => {
    const records = [];
    const calcLateDays = (report, end) => {
      // Trễ nếu ngày báo cáo > ngày kết thúc
      const reportDate = report ? new Date(report) : null;
      const endDate = end ? new Date(end) : null;
      if (!reportDate || !endDate) return 0;

      const msPerDay = 1000 * 60 * 60 * 24;
      // Chỉ tính theo ngày (bọ tròn xuống về 00:00)
      const reportDay = new Date(
        reportDate.getFullYear(),
        reportDate.getMonth(),
        reportDate.getDate(),
      );
      const endDay = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
      );

      const diffDays = Math.ceil((reportDay - endDay) / msPerDay); // dương nếu trễ
      return diffDays > 0 ? diffDays : 0;
    };

    source.forEach((wo) => {
      const normalizedStatus = (wo.wo_status || "").trim();
      // Không gửi về BE các công việc chưa có trạng thái xác định
      if (!normalizedStatus || normalizedStatus === "Chưa xác định") {
        return;
      }

      const assignees =
        wo.wo_assignee && wo.wo_assignee.length > 0 ? wo.wo_assignee : [null];
      assignees.forEach((assignee, idx) => {
        const hasMultiple = assignees.length > 1;
        const maCV = hasMultiple ? `${wo.wo_id}!${idx + 1}` : wo.wo_id;

        // Trễ: số ngày trễ dương -> BCSomTre âm; đúng hạn/sớm: 0
        const lateDays = calcLateDays(wo.wo_date_report, wo.wo_date_end);
        const bcSomTre = lateDays > 0 ? -lateDays : 0;

        // Điểm tiến độ dùng BCSomTre: mỗi ngày trễ trừ 2 điểm, tối thiểu 0
        const diemTienDo = Math.max(0, 10 + bcSomTre * 2);

        const ngayHoanThanh = wo.wo_date_report || null;
        const ngayKetThuc = wo.wo_date_end || null;
        records.push({
          MaCV_FW: maCV || "",
          MaNV: normalizeUserCode(assignee?.email),
          BCSomTre: bcSomTre,
          Loi: 0,
          SoLoi: 0,
          ChiTietLoi: "",
          NhanXet: "",
          DiemTienDo: diemTienDo,
          DiemChatLuong: Number(wo.wo_evaluator_score) || 0,
          AutKPI: 0,
          NgayChamDiem: ngayHoanThanh,
          Web: "",
          MaNVQL_FW: normalizeUserCode(wo.wo_creator?.email),
          MaNVCD_FW: normalizeUserCode(wo.wo_evaluator_by?.email),
          NoiDungCV_FW: `${wo.wo_name || ""}${
            wo.wo_desc ? `: ${wo.wo_desc}` : ""
          }`,
          NgayHT_FW: ngayHoanThanh,
          TrangThai_FW: normalizedStatus,
          TienDo_FW: Number(wo.wo_completed) || 0,
          NgayDeadline_FW: ngayKetThuc,
        });
      });
    });
    return records;
  };

  const appendLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setSavingLog((prev) => [...prev, `${timestamp} - ${message}`]);
  };

  const handleSaveCurrentPage = async () => {
    setSaving(true);
    setShowSavingModal(true);
    setSaveMessage(null);
    setSavingLog([]);
    try {
      const firstPage = await fetchWorkOrdersPage(1);
      const pageCache = { 1: firstPage };
      const totalPagesLocal = Math.max(
        1,
        Math.ceil((firstPage.count || firstPage.list.length) / limit),
      );
      setSaveProgress({ current: 0, total: totalPagesLocal });
      let totalProcessed = 0;

      for (
        let startPage = 1;
        startPage <= totalPagesLocal;
        startPage += BATCH_PAGE_SIZE
      ) {
        const endPage = Math.min(
          startPage + BATCH_PAGE_SIZE - 1,
          totalPagesLocal,
        );
        appendLog(
          `Đang xử lý dữ liệu trang ${startPage}${
            startPage !== endPage ? `-${endPage}` : ""
          }...`,
        );
        let batchRecords = [];

        for (let page = startPage; page <= endPage; page++) {
          const pageData = pageCache[page] || (await fetchWorkOrdersPage(page));
          pageCache[page] = pageData;
          // Filter list to only include items within date range
          const filteredPageList = pageData.list.filter((wo) =>
            isDateInRange(wo.wo_date_end, fromDate, toDate),
          );
          batchRecords.push(...buildEvaluationRecords(filteredPageList));
          setSaveProgress({ current: page, total: totalPagesLocal });
        }

        if (!batchRecords.length) {
          appendLog(`Không có dữ liệu ở batch trang ${startPage}-${endPage}.`);
          continue;
        }

        appendLog(
          `Gửi ${batchRecords.length} bản ghi (trang ${startPage}-${endPage}) lên server...`,
        );
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/fastwork/save-work-evaluation`,
          {
            method: "POST",
            headers: getAuthHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify(batchRecords),
          },
        );
        const data = await response.json();
        if (!response.ok || data.success === false)
          throw new Error(
            data.message ||
              `Lưu thất bại ở batch trang ${startPage}-${endPage}`,
          );

        const processedCount = data.processed || batchRecords.length;
        totalProcessed += processedCount;
        appendLog(
          `Lưu thành công batch trang ${startPage}-${endPage}: ${processedCount} bản ghi.`,
        );
      }

      if (totalProcessed === 0) {
        setSaveMessage({ type: "warning", text: "Không có dữ liệu để lưu." });
      } else {
        setSaveMessage({
          type: "success",
          text: `Đã lưu ${totalProcessed} bản ghi thành công`,
        });
      }
    } catch (err) {
      appendLog(`Lỗi: ${err.message || "Có lỗi khi lưu dữ liệu"}`);
      setSaveMessage({
        type: "danger",
        text: err.message || "Có lỗi khi lưu dữ liệu",
      });
    } finally {
      setSaving(false);
      setShowSavingModal(false);
    }
  };

  return (
    <>
      {/* Bootstrap CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {/* Custom CSS for text truncation */}
      <style>
        {`
          .text-truncate-multiline {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            line-height: 1.2em;
            max-height: 2.4em;
          }
          
          .text-truncate-single {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 150px;
          }
          
          .assignee-text {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 120px;
          }
          
          .creator-text {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 120px;
          }
          
          .table-row-height {
            height: 80px;
            vertical-align: middle;
          }

          .import-login-backdrop {
            background: rgba(15, 23, 42, 0.72);
            backdrop-filter: blur(4px);
            z-index: 2000;
          }

          .import-login-backdrop .modal-dialog {
            max-width: 430px;
          }

          .import-login-backdrop .modal-content {
            border: 0;
            border-radius: 18px;
            overflow: hidden;
            box-shadow: 0 24px 64px rgba(15, 23, 42, 0.35);
          }

          .import-login-icon {
            width: 56px;
            height: 56px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            color: #0d6efd;
            background: rgba(13, 110, 253, 0.1);
            font-size: 1.6rem;
          }

          .import-auth-trigger {
            position: fixed;
            top: 16px;
            right: 16px;
            z-index: 1050;
            min-height: 48px;
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 8px 16px 8px 10px;
            border-radius: 14px;
            font-weight: 700;
            line-height: 1.1;
            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.2);
            transition:
              transform 0.2s ease,
              box-shadow 0.2s ease;
          }

          .import-auth-trigger:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 28px rgba(15, 23, 42, 0.26);
          }

          .import-login-trigger {
            border: 1px solid rgba(255, 255, 255, 0.3);
            color: #fff;
            background: linear-gradient(135deg, #2563eb, #1d4ed8);
          }

          .import-login-trigger:hover,
          .import-login-trigger:focus {
            color: #fff;
            background: linear-gradient(135deg, #1d4ed8, #1e40af);
          }

          .import-logout-trigger {
            border: 1px solid #fecaca;
            color: #b42318;
            background: #fff;
          }

          .import-logout-trigger:hover,
          .import-logout-trigger:focus {
            color: #991b1b;
            background: #fff5f5;
          }

          .import-auth-trigger-icon {
            width: 32px;
            height: 32px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 10px;
            background: rgba(255, 255, 255, 0.16);
            font-size: 1rem;
          }

          .import-logout-trigger .import-auth-trigger-icon {
            background: #fee2e2;
          }

          .import-login-close {
            position: absolute;
            top: 14px;
            right: 14px;
            z-index: 5;
            width: 38px;
            height: 38px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 0;
            border-radius: 50%;
            color: #475569;
            background: #f1f5f9;
            font-size: 1.1rem;
          }

          .import-login-close:hover,
          .import-login-close:focus {
            color: #0f172a;
            background: #e2e8f0;
          }

          @media (max-width: 575.98px) {
            .import-auth-trigger {
              top: 10px;
              right: 10px;
            }
          }
        `}
      </style>

      {!isAuthenticated && (
        <button
          type="button"
          className="btn import-auth-trigger import-login-trigger"
          onClick={() => {
            setLoginError("");
            setShowLoginModal(true);
          }}
        >
          <span className="import-auth-trigger-icon">
            <i className="bi bi-person-lock"></i>
          </span>
          <span>Đăng nhập</span>
        </button>
      )}

      {isAuthenticated && (
        <button
          type="button"
          className="btn import-auth-trigger import-logout-trigger"
          onClick={handleLogout}
        >
          <span className="import-auth-trigger-icon">
            <i className="bi bi-box-arrow-right"></i>
          </span>
          <span>Đăng xuất</span>
        </button>
      )}

      <div className="container-fluid py-4">
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-primary text-white">
                <h4 className="mb-1 d-flex align-items-center">
                  <i className="bi bi-list-task me-2"></i>
                  Quản lý công việc
                </h4>
                <small className="opacity-75">
                  Danh sách các công việc được giao
                </small>
              </div>

              <div className="card-body">
                {/* Filter Form */}
                <div className="row g-3 mb-4 p-3 bg-light rounded">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-calendar-date me-1"></i>
                      Từ ngày
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-calendar-date me-1"></i>
                      Đến ngày
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-person-lines-fill me-1"></i>
                      Tài khoản người dùng
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Nhập username, cách nhau bởi dấu phẩy"
                      value={usernames}
                      onChange={handleUsernamesChange}
                      disabled={viewAll}
                    />
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleSearch}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Tìm kiếm...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-search me-2"></i>
                          Tìm kiếm
                        </>
                      )}
                    </button>
                  </div>
                  {canSaveToDatabase && (
                    <div className="col-md-2 d-flex align-items-end">
                      <button
                        type="button"
                        className="btn btn-success w-100"
                        onClick={handleSaveCurrentPage}
                        disabled={loading || saving}
                      >
                        {saving ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                            Đang lưu...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-cloud-arrow-down me-2"></i>
                            Lưu dữ liệu
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                <div className="row g-3 mb-4">
                  {!hasUserFromUrl && (
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="viewAllCheck"
                          checked={viewAll}
                          onChange={(e) => {
                            setViewAll(e.target.checked);
                            // Clear usernames when checking "view all"
                            if (e.target.checked) {
                              setUsernames("");
                            }
                          }}
                        />
                        <label
                          className="form-check-label fw-semibold"
                          htmlFor="viewAllCheck"
                        >
                          <i className="bi bi-eye me-1"></i>
                          Xem tất cả (admin@doticom.vn)
                        </label>
                      </div>
                    </div>
                  )}
                  <div className={hasUserFromUrl ? "col-md-12" : "col-md-6"}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="evaluateCheck"
                        checked={evaluateOnly}
                        onChange={(e) => setEvaluateOnly(e.target.checked)}
                      />
                      <label
                        className="form-check-label fw-semibold"
                        htmlFor="evaluateCheck"
                      >
                        <i className="bi bi-clipboard-check me-1"></i>
                        Chỉ hiển thị công việc cần đánh giá
                      </label>
                    </div>
                  </div>
                </div>

                {error && (
                  <div
                    className="alert alert-danger d-flex align-items-center"
                    role="alert"
                  >
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                  </div>
                )}

                {saveMessage && (
                  <div
                    className={`alert alert-${saveMessage.type} d-flex align-items-center`}
                    role="alert"
                  >
                    <i className="bi bi-info-circle me-2"></i>
                    {saveMessage.text}
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div>Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive shadow-sm rounded">
                      <table className="table table-hover table-striped mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th scope="col" style={{ width: "10%" }}>
                              Mã công việc
                            </th>
                            <th scope="col" style={{ width: "20%" }}>
                              Tên công việc
                            </th>
                            <th scope="col" style={{ width: "15%" }}>
                              Người thực hiện
                            </th>
                            <th scope="col" style={{ width: "15%" }}>
                              Người giao việc
                            </th>
                            <th scope="col" style={{ width: "12%" }}>
                              Người đánh giá
                            </th>
                            <th scope="col" style={{ width: "8%" }}>
                              Trạng thái
                            </th>
                            <th scope="col" style={{ width: "12%" }}>
                              Thời gian
                            </th>
                            <th scope="col" style={{ width: "8%" }}>
                              Tiến độ
                            </th>
                            <th scope="col" style={{ width: "5%" }}>
                              Điểm
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredWorkOrders?.length ? (
                            filteredWorkOrders.map((wo) => (
                              <tr key={wo.wo_id} className="table-row-height">
                                <td>
                                  <div
                                    className="fw-bold text-primary cursor-pointer text-truncate-single"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleWorkOrderClick(wo)}
                                    title={`${
                                      wo.wo_code || wo.wo_id || "N/A"
                                    } - Click để xem nội dung công việc`}
                                  >
                                    <i className="bi bi-file-text me-1"></i>
                                    {wo.wo_code || wo.wo_id || "N/A"}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    className="small text-truncate-multiline"
                                    title={wo.wo_name}
                                    style={{ lineHeight: "1.2" }}
                                  >
                                    {wo.wo_name}
                                  </div>
                                </td>
                                <td>
                                  {wo.wo_assignee &&
                                    wo.wo_assignee
                                      .slice(0, 2)
                                      .map((assignee, index) => (
                                        <div
                                          key={index}
                                          className="d-flex align-items-center mb-1"
                                          style={{ minHeight: "32px" }}
                                        >
                                          <img
                                            src={assignee.avatar}
                                            alt=""
                                            className="rounded-circle me-2 border flex-shrink-0"
                                            style={{
                                              width: "24px",
                                              height: "24px",
                                              objectFit: "cover",
                                            }}
                                            onError={(e) => {
                                              e.target.src =
                                                "https://app.fastwork.vn/male.png";
                                            }}
                                          />
                                          <div className="assignee-text">
                                            <div
                                              className="fw-semibold small text-truncate"
                                              title={assignee.name}
                                            >
                                              {assignee.name}
                                            </div>
                                            <div
                                              className="text-muted text-truncate"
                                              style={{ fontSize: "0.7rem" }}
                                              title={assignee.email}
                                            >
                                              {assignee.email}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                  {wo.wo_assignee &&
                                    wo.wo_assignee.length > 2 && (
                                      <div className="small text-muted">
                                        +{wo.wo_assignee.length - 2} người khác
                                      </div>
                                    )}
                                </td>
                                <td>
                                  <div
                                    className="d-flex align-items-center"
                                    style={{ minHeight: "32px" }}
                                  >
                                    <img
                                      src={wo.wo_creator?.avatar}
                                      alt=""
                                      className="rounded-circle me-2 border flex-shrink-0"
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        objectFit: "cover",
                                      }}
                                      onError={(e) => {
                                        e.target.src =
                                          "https://app.fastwork.vn/male.png";
                                      }}
                                    />
                                    <div className="creator-text">
                                      <div
                                        className="fw-semibold small text-truncate"
                                        title={wo.wo_creator?.name}
                                      >
                                        {wo.wo_creator?.name}
                                      </div>
                                      <div
                                        className="text-muted text-truncate"
                                        style={{ fontSize: "0.7rem" }}
                                        title={wo.wo_creator?.email}
                                      >
                                        {wo.wo_creator?.email}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  {wo.wo_evaluator_by ? (
                                    <div
                                      className="d-flex align-items-center"
                                      style={{ minHeight: "32px" }}
                                    >
                                      <img
                                        src={
                                          wo.wo_evaluator_by.avatar ||
                                          "https://app.fastwork.vn/male.png"
                                        }
                                        alt=""
                                        className="rounded-circle me-2 border flex-shrink-0"
                                        style={{
                                          width: "24px",
                                          height: "24px",
                                          objectFit: "cover",
                                        }}
                                        onError={(e) => {
                                          e.target.src =
                                            "https://app.fastwork.vn/male.png";
                                        }}
                                      />
                                      <div className="creator-text">
                                        <div
                                          className="fw-semibold small text-truncate"
                                          title={wo.wo_evaluator_by.name}
                                        >
                                          {wo.wo_evaluator_by.name}
                                        </div>
                                        <div
                                          className="text-muted text-truncate"
                                          style={{ fontSize: "0.7rem" }}
                                          title={wo.wo_evaluator_by.email}
                                        >
                                          {wo.wo_evaluator_by.email}
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-muted small">
                                      Chưa có
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <div>
                                    <span
                                      className={`badge ${getStatusColor(
                                        wo.wo_status,
                                      )} text-truncate`}
                                      style={{ fontSize: "0.7rem" }}
                                    >
                                      {wo.wo_status || "Chưa xác định"}
                                    </span>
                                    {wo.wo_delay_hours > 0 && (
                                      <div className="mt-1">
                                        <span
                                          className="badge bg-warning text-dark"
                                          style={{ fontSize: "0.6rem" }}
                                        >
                                          <i className="bi bi-clock me-1"></i>
                                          {formatDelayHours(wo.wo_delay_hours)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    className="small"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    <div className="mb-1 text-truncate">
                                      <i className="bi bi-play-circle text-success me-1"></i>
                                      {formatDate(wo.wo_date_start)}
                                    </div>
                                    <div className="mb-1 text-truncate">
                                      <i className="bi bi-stop-circle text-danger me-1"></i>
                                      {formatDate(wo.wo_date_end)}
                                    </div>
                                    {wo.wo_date_report && (
                                      <div className="text-success text-truncate">
                                        <i className="bi bi-file-earmark-check me-1"></i>
                                        {formatDate(wo.wo_date_report)}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="mb-2">
                                    <div
                                      className="progress"
                                      style={{ height: "6px" }}
                                    >
                                      <div
                                        className="progress-bar bg-info progress-bar-striped"
                                        role="progressbar"
                                        style={{
                                          width: `${wo.wo_completed || 0}%`,
                                        }}
                                        aria-valuenow={wo.wo_completed || 0}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                      ></div>
                                    </div>
                                    <div className="text-center mt-1">
                                      <span
                                        className="small fw-bold text-info"
                                        style={{ fontSize: "0.7rem" }}
                                      >
                                        {wo.wo_completed || 0}%
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    className="small text-muted text-center text-truncate"
                                    style={{ fontSize: "0.6rem" }}
                                    title={wo.wo_evaluator_progress}
                                  >
                                    {wo.wo_evaluator_progress ||
                                      "Chưa đánh giá"}
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="d-flex flex-column align-items-center">
                                    <span
                                      className="h6 text-primary fw-bold mb-0"
                                      style={{ fontSize: "0.9rem" }}
                                    >
                                      {wo.wo_evaluator_score || 0}
                                    </span>
                                    <small
                                      className="text-muted"
                                      style={{ fontSize: "0.6rem" }}
                                    >
                                      /10
                                    </small>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="9"
                                className="text-center text-muted py-4"
                              >
                                <i className="bi bi-inbox me-2"></i>
                                Không có công việc nào nằm trong khoảng thời
                                gian từ {fromDate} đến {toDate}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && filteredWorkOrders.length > 0 && (
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
                        <div className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          Hiển thị trang <strong>{currentPage}</strong> /{" "}
                          <strong>{totalPages}</strong> - Tổng cộng{" "}
                          <strong>{totalCount}</strong> bản ghi
                        </div>
                        <nav aria-label="Page navigation">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                              >
                                <i className="bi bi-chevron-double-left"></i>{" "}
                                Đầu
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                              >
                                <i className="bi bi-chevron-left"></i> Trước
                              </button>
                            </li>

                            {[...Array(Math.min(5, totalPages))].map(
                              (_, index) => {
                                const pageNum =
                                  Math.max(1, currentPage - 2) + index;
                                if (pageNum <= totalPages) {
                                  return (
                                    <li
                                      key={pageNum}
                                      className={`page-item ${
                                        pageNum === currentPage ? "active" : ""
                                      }`}
                                    >
                                      <button
                                        className="page-link"
                                        onClick={() =>
                                          handlePageChange(pageNum)
                                        }
                                      >
                                        {pageNum}
                                      </button>
                                    </li>
                                  );
                                }
                                return null;
                              },
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                              >
                                Sau <i className="bi bi-chevron-right"></i>
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                              >
                                Cuối{" "}
                                <i className="bi bi-chevron-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Work Order Detail Modal */}
      {showModal && selectedWorkOrder && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title d-flex align-items-center">
                  <i className="bi bi-file-text-fill me-2"></i>
                  Chi tiết công việc:{" "}
                  {selectedWorkOrder.wo_code ||
                    selectedWorkOrder.wo_id ||
                    "N/A"}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-4">
                  {/* Basic Information */}
                  <div className="col-12">
                    <div className="card border-0 bg-light">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-4">
                            <h6 className="text-primary fw-bold mb-2">
                              <i className="bi bi-hash me-1"></i>
                              Mã công việc
                            </h6>
                            <p className="mb-3">
                              {selectedWorkOrder.wo_code ||
                                selectedWorkOrder.wo_id ||
                                "N/A"}
                            </p>
                          </div>
                          <div className="col-md-4">
                            <h6 className="text-primary fw-bold mb-2">
                              <i className="bi bi-clipboard me-1"></i>
                              Trạng thái
                            </h6>
                            <div className="mb-2">
                              <span
                                className={`badge ${getStatusColor(
                                  selectedWorkOrder.wo_status,
                                )}`}
                              >
                                {selectedWorkOrder.wo_status || "Chưa xác định"}
                              </span>
                            </div>
                            {selectedWorkOrder.wo_delay_hours > 0 && (
                              <div>
                                <span className="badge bg-warning text-dark">
                                  <i className="bi bi-clock-history me-1"></i>
                                  Trễ hạn:{" "}
                                  {formatDelayHours(
                                    selectedWorkOrder.wo_delay_hours,
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="col-md-4">
                            <h6 className="text-primary fw-bold mb-2">
                              <i className="bi bi-star-fill me-1"></i>
                              Điểm đánh giá
                            </h6>
                            <div className="text-center">
                              <span className="h4 text-primary fw-bold">
                                {selectedWorkOrder.wo_evaluator_score || 0}/10
                              </span>
                              <div className="small text-muted">
                                {selectedWorkOrder.wo_evaluator_progress ||
                                  "Chưa đánh giá"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Work Order Name */}
                  <div className="col-12">
                    <h6 className="text-primary fw-bold mb-2">
                      <i className="bi bi-card-heading me-1"></i>
                      Tên công việc
                    </h6>
                    <p className="mb-3 p-3 bg-light rounded">
                      {selectedWorkOrder.wo_name}
                    </p>
                  </div>

                  {/* Work Order Description (Content) */}
                  <div className="col-12">
                    <h6 className="text-primary fw-bold mb-2">
                      <i className="bi bi-file-earmark-text me-1"></i>
                      Nội dung công việc (Mô tả chi tiết)
                    </h6>
                    <div className="card border border-primary">
                      <div className="card-body bg-light">
                        <p
                          className="mb-0"
                          style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}
                        >
                          {selectedWorkOrder.wo_desc ||
                            "Không có mô tả chi tiết"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* People Information */}
                  <div className="col-md-4">
                    <h6 className="text-primary fw-bold mb-2">
                      <i className="bi bi-person-check me-1"></i>
                      Người thực hiện
                    </h6>
                    {selectedWorkOrder.wo_assignee &&
                      selectedWorkOrder.wo_assignee.map((assignee, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center mb-3 p-2 border rounded"
                        >
                          <img
                            src={assignee.avatar}
                            alt=""
                            className="rounded-circle me-3 border"
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src = "https://app.fastwork.vn/male.png";
                            }}
                          />
                          <div>
                            <div className="fw-bold">{assignee.name}</div>
                            <div className="text-muted small">
                              {assignee.email}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>

                  <div className="col-md-4">
                    <h6 className="text-primary fw-bold mb-2">
                      <i className="bi bi-person-plus me-1"></i>
                      Người giao việc
                    </h6>
                    <div className="d-flex align-items-center p-2 border rounded">
                      <img
                        src={selectedWorkOrder.wo_creator?.avatar}
                        alt=""
                        className="rounded-circle me-3 border"
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.src = "https://app.fastwork.vn/male.png";
                        }}
                      />
                      <div>
                        <div className="fw-bold">
                          {selectedWorkOrder.wo_creator?.name}
                        </div>
                        <div className="text-muted small">
                          {selectedWorkOrder.wo_creator?.email}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <h6 className="text-primary fw-bold mb-2">
                      <i className="bi bi-person-badge me-1"></i>
                      Người đánh giá
                    </h6>
                    {selectedWorkOrder.wo_evaluator_by ? (
                      <div className="d-flex align-items-center p-2 border rounded">
                        <img
                          src={
                            selectedWorkOrder.wo_evaluator_by.avatar ||
                            "https://app.fastwork.vn/male.png"
                          }
                          alt=""
                          className="rounded-circle me-3 border"
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.target.src = "https://app.fastwork.vn/male.png";
                          }}
                        />
                        <div>
                          <div className="fw-bold">
                            {selectedWorkOrder.wo_evaluator_by.name}
                          </div>
                          <div className="text-muted small">
                            {selectedWorkOrder.wo_evaluator_by.email}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 border rounded text-center text-muted">
                        <i className="bi bi-person-x fs-2 mb-2"></i>
                        <div>Chưa có người đánh giá</div>
                      </div>
                    )}
                  </div>

                  {/* Timeline and Progress */}
                  <div className="col-md-8">
                    <h6 className="text-primary fw-bold mb-3">
                      <i className="bi bi-calendar-range me-1"></i>
                      Thời gian thực hiện
                    </h6>
                    <div className="row g-2">
                      <div className="col-12">
                        <div className="d-flex align-items-center mb-2 p-2 border-start border-success border-3 bg-light">
                          <i className="bi bi-play-circle text-success me-2"></i>
                          <strong>Bắt đầu:</strong>&nbsp;
                          {formatDate(selectedWorkOrder.wo_date_start)}
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="d-flex align-items-center mb-2 p-2 border-start border-danger border-3 bg-light">
                          <i className="bi bi-stop-circle text-danger me-2"></i>
                          <strong>Kết thúc:</strong>&nbsp;
                          {formatDate(selectedWorkOrder.wo_date_end)}
                        </div>
                      </div>
                      {selectedWorkOrder.wo_date_report && (
                        <div className="col-12">
                          <div className="d-flex align-items-center mb-2 p-2 border-start border-info border-3 bg-light">
                            <i className="bi bi-file-earmark-check text-info me-2"></i>
                            <strong>Ngày báo cáo:</strong>&nbsp;
                            {formatDate(selectedWorkOrder.wo_date_report)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <h6 className="text-primary fw-bold mb-3">
                      <i className="bi bi-graph-up me-1"></i>
                      Tiến độ thực hiện
                    </h6>
                    <div className="text-center p-3 border rounded bg-light">
                      <div className="progress mb-3" style={{ height: "15px" }}>
                        <div
                          className="progress-bar bg-info progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          style={{
                            width: `${selectedWorkOrder.wo_completed || 0}%`,
                          }}
                          aria-valuenow={selectedWorkOrder.wo_completed || 0}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        ></div>
                      </div>
                      <h4 className="text-info mb-2">
                        {selectedWorkOrder.wo_completed || 0}%
                      </h4>
                      <div className="small text-muted">
                        {selectedWorkOrder.wo_evaluator_progress ||
                          "Chưa đánh giá"}
                      </div>
                    </div>
                  </div>

                  {/* Followers */}
                  {selectedWorkOrder.wo_follow &&
                    selectedWorkOrder.wo_follow.length > 0 && (
                      <div className="col-12">
                        <h6 className="text-primary fw-bold mb-2">
                          <i className="bi bi-people me-1"></i>
                          Người theo dõi ({selectedWorkOrder.wo_follow.length})
                        </h6>
                        <div className="row g-2">
                          {selectedWorkOrder.wo_follow.map(
                            (follower, index) => (
                              <div key={index} className="col-md-6 col-lg-4">
                                <div className="d-flex align-items-center p-2 border rounded bg-light">
                                  <img
                                    src={follower.avatar}
                                    alt=""
                                    className="rounded-circle me-2 border"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      e.target.src =
                                        "https://app.fastwork.vn/male.png";
                                    }}
                                  />
                                  <div>
                                    <div className="fw-semibold small">
                                      {follower.name}
                                    </div>
                                    <div
                                      className="text-muted"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {follower.email}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Saving Modal */}
      {showSavingModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-body">
                <div className="d-flex align-items-center mb-3">
                  <div
                    className="spinner-border text-primary me-3"
                    role="status"
                  ></div>
                  <div>
                    <h6 className="mb-1">Đang lưu dữ liệu...</h6>
                    <small className="text-muted">
                      Trang {saveProgress.current}/{saveProgress.total}
                    </small>
                  </div>
                </div>
                <div
                  className="border rounded p-2 bg-light"
                  style={{ maxHeight: "240px", overflowY: "auto" }}
                >
                  {savingLog.length === 0 ? (
                    <div className="text-muted small">Chưa có log...</div>
                  ) : (
                    savingLog.map((log, idx) => (
                      <div key={idx} className="small text-secondary">
                        {log}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isAuthenticated && showLoginModal && (
        <div
          className="modal fade show d-block import-login-backdrop"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="import-login-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              handleCloseLoginModal();
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              handleCloseLoginModal();
            }
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <button
                type="button"
                className="import-login-close"
                aria-label="Đóng"
                onClick={handleCloseLoginModal}
              >
                <i className="bi bi-x-lg"></i>
              </button>
              <form onSubmit={handleLogin} noValidate>
                <div className="modal-body p-4 p-md-5">
                  <div className="text-center mb-4">
                    <div className="import-login-icon mb-3">
                      <i className="bi bi-shield-lock"></i>
                    </div>
                    <h4 id="import-login-title" className="fw-bold mb-2">
                      Đăng nhập
                    </h4>
                    <p className="small fw-semibold text-primary mt-2 mb-0">
                      Vui lòng đăng nhập bằng tài khoản Doticom
                    </p>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="import-login-username" className="form-label">
                      Tên đăng nhập
                    </label>
                    <input
                      id="import-login-username"
                      type="text"
                      className="form-control form-control-lg"
                      value={loginUsername}
                      onChange={(event) => setLoginUsername(event.target.value)}
                      autoComplete="username"
                      autoFocus
                      disabled={loggingIn}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="import-login-password" className="form-label">
                      Mật khẩu
                    </label>
                    <div className="input-group input-group-lg">
                      <input
                        id="import-login-password"
                        type={showLoginPassword ? "text" : "password"}
                        className="form-control"
                        value={loginPassword}
                        onChange={(event) =>
                          setLoginPassword(event.target.value)
                        }
                        autoComplete="current-password"
                        disabled={loggingIn}
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() =>
                          setShowLoginPassword((current) => !current)
                        }
                        aria-label={
                          showLoginPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"
                        }
                        disabled={loggingIn}
                      >
                        <i
                          className={`bi ${
                            showLoginPassword ? "bi-eye-slash" : "bi-eye"
                          }`}
                        ></i>
                      </button>
                    </div>
                  </div>

                  {loginError && (
                    <div className="alert alert-danger py-2" role="alert">
                      {loginError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100"
                    disabled={loggingIn}
                  >
                    {loggingIn ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Đang đăng nhập...
                      </>
                    ) : (
                      "Đăng nhập"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bootstrap Icons */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css"
        rel="stylesheet"
      />
    </>
  );
};

export default ImportDiemCV;
